// src/pages/SubscriptionOptions/SubscriptionOptions.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { MdArrowBack } from "react-icons/md";
import { toast } from "react-toastify";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AOS from "aos";
import "aos/dist/aos.css";
// Huom. varmista että tiedostonimi & hakemisto täsmäävät!
import SubscriptionCheckoutForm from "../../components/SubscriptionCheckoutForm/SubscriptionCheckoutForm";

// Ominaisuustaulukon data
const features = [
  ["Provisio keikkamyynnistä", "15 %", "10 %"],
  ["Genreraja profiilissa", "1 genre", "4 genreä"],
  ["Esiintymisalueita", "1 alue", "4 aluetta"],
  ["Settivaihtoehtoja", "1 kpl", "ei rajoitusta"],
  ["Automaattiset tarjouspyynnöt", "✅", "✅"],
  ["Palautelomake", "✅", "✅"],
  ["Tähtiarvostelut (rating)", "❌", "✅"],
  ["Some-linkit", "❌", "✅"],
  ["Kuvagalleria", "❌", "6 kuvaa"],
  ["Videolinkki", "❌", "✅"],
  ["Mp3-soitin", "❌", "✅"],
  ["Soundcloud-linkki", "1 linkki", "4 linkkiä"],
  ["Keikkakalenteri", "✅", "✅"],
  ["Omat palvelut", "❌", "✅"],
  ["Asiakasviestintä", "✅", "✅"],
  ["Viikon esiintyjä", "❌", "✅"],
  ["Pääkuva", "✅", "✅"],
  ["Esittelyteksti", "✅", "✅"],
  ["Laitteiden vuokraus – 10 %", "❌", "✅"],
];

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

export default function SubscriptionOptions() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState("");
  const [paymentDone, setPaymentDone] = useState(false);

  // summa sentteinä
  const amount =
    selectedPlan === "Basic"   ? 695
  : selectedPlan === "Premium" ? 1695
  : 0;

  const handlePaymentSuccess = () => {
    toast.success("Maksu onnistui! Ohjataan dashboardiin…");
    setPaymentDone(true);
    setTimeout(() => navigate("/performer-dashboard"), 1000);
  };

  useEffect(() => {
    AOS.init({
      duration: 700,
      once: true, // animoi vain kerran
    });
  }, []);

  return (
    <div className="pt-24 pb-12 px-4 dark:bg-purple-950 text-darkest dark:text-primary dark:bg-opacity-75">
      <Helmet>
        <title>Spiderlegs Productions | Liity mukaan esiintyjäverkostoon</title>
        <meta
          name="description"
          content="Valitse esiintyjätilaus, maksa kortilla ja pääset muokkaamaan profiiliasi heti!"
        />
      </Helmet>

      {/* ← Takaisin-painike */}
      <ResponsiveButton
        onClick={() => {
          if (window.history.length > 2) navigate(-1);
          else navigate("/");
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
        }}
        variant="backbutton"
        className="ml-4"
      >
        <div className="flex items-center gap-2">
          <MdArrowBack className="text-xl" />
          <span>Takaisin</span>
        </div>
      </ResponsiveButton>

      <h1 className="font-customFont2 text-center text-5xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 pt-8 dark:text-shadow-xxl">
        Valitse esiintyjätilaus
      </h1>

      <p className="max-w-3xl mx-auto mb-8 font-customFont1 dark:text-shadow-lg text-center text-darkest dark:text-primary text-lg">
        Valitse itsellesi sopivin jäsenyystaso. Maksun jälkeen pääset heti muokkaamaan
        profiiliasi dashboardissa!
      </p>

      {/* — Kortit Basic & Premium — */}
      <div className="grid md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-8 max-w-6xl mx-auto pb-10">
        {["Basic", "Premium"].map((plan) => (
        <div
        key={plan}
        role="button"
        tabIndex={0}
        
        // 🔽 Tyyliluokat: border, pyöristetyt kulmat, keskitys, hover-efekti
        className={`border p-5 rounded-3xl text-center cursor-pointer hover:scale-105 transition
        ${
            selectedPlan === plan
            ? plan === "Basic"

                ? "border-8 border-violet bg-darkest rounded-3xl p-4 ring-4 text-primary" 
                // Valittu Basic
                
                : "border-8 border-yellow-500 bg-darkest rounded-3xl p-4 ring-4 text-primary"


            : "border-gray-300 bg-white dark:bg-violet dark:bg-opacity-75" // Ei valittu
        }`
        }

        // 🔽 Klikattaessa asetetaan valittu plan ja scrollataan lomakkeeseen
        onClick={() => {
        setSelectedPlan(plan);
        setPaymentDone(false);
        setTimeout(() => {
            document.getElementById("join-form-section")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        }}

        // 🔽 Näppäimistötuki (Enter tai välilyönti)
        onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setSelectedPlan(plan);
        }}
        >

        {/* Otsikko väritetty planin mukaan */}
        <h2 className={`text-3xl font-customFont2 font-bold mb-2 ${plan === "Basic" ? "text-purple-700" : "text-yellow-500"}`}>
        {plan}
        </h2>

        {/* Kuukausihinta */}
        <p className="font-customFont1 font-semibold mb-4 text-3xl">
        {plan === "Basic" ? "6,95 €/kk" : "16,95 €/kk"}
        </p>

        {/* Ominaisuudet listana */}
        <ul className="list-none space-y-1 font-customFont1 text-lg">
        {plan === "Basic" ? (
            <>
            <p></p>
            <li>Näkyvyys sivustolla</li>
            <li>Profiili ja yhteystiedot</li>
            <li>Keikkakalenteri</li>
            <li>Yhteydenottolomake</li>
            </>
        ) : (
            <>
            <li>Basic-edut</li>
            <li>Galleria ja videot</li>
            <li>Ääninäytteet</li>
            <li>Palautteet ja arvostelut</li>
            <li>Some-linkit</li>
            <li>Premium-badge ja parempi näkyvyys</li>
            </>
        )}
        </ul>

        </div>
        ))}
      </div>

      {/* — Taulukko + maksu — */}
      <div className="grid md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-8 max-w-7xl mx-auto">

        {/* Ominaisuustaulukko */}
        <div className="overflow-x-auto rounded-3xl">

          <table className="
          w-full 
          table-auto 
          border-collapse 
          shadow-lg 
          bg-white 
          dark:bg-violet 
          text-sm 
          rounded-3xl
          ">

            <thead className="bg-darkest">
              <tr>
                <th className="px-4 py-2 text-left font-bold text-white">Ominaisuus</th>
                <th className="px-4 py-2 text-center font-bold text-purple-600">Basic</th>
                <th className="px-4 py-2 text-center font-bold text-yellow-500">Premium</th>
              </tr>
            </thead>
            <tbody>
              {features.map(([feature, basic, premium], idx) => (
                <tr key={idx} className="border-t border-gray-300 dark:border-gray-700">
                  <td className="px-4 py-2 font-customFont1 dark:text-white">{feature}</td>
                  <td className="px-4 py-2 text-center font-customFont1 dark:text-white">{basic}</td>
                  <td className="px-4 py-2 text-center font-customFont1 dark:text-white">{premium}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Stripe-maksu */}
        {selectedPlan && amount > 0 && !paymentDone && (
  <div
    id="join-form-section"
    className="scroll-mt-10 overflow-x-auto rounded-3xl w-full"
    data-aos="fade-in"
  >
    <div className="w-full border border-gray-300 dark:border-gray-700 shadow-lg bg-white dark:bg-violet p-6 rounded-3xl h-full">
      <Elements stripe={stripePromise}>
        <SubscriptionCheckoutForm
          plan={selectedPlan.toLowerCase()}
          amount={amount}
          onSuccess={handlePaymentSuccess}
        />
      </Elements>
    </div>
  </div>
)}


        {/* Maksun jälkeen */}
        {paymentDone && (
          <div className="flex items-center justify-center">
            <p className="text-lg font-customFont1 text-darkest dark:text-primary">
              Kiitos! Maksu on suoritettu – ohjataan pian dashboardiin…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
